import { useCallback, useMemo } from 'react'
import { useQueryParams } from 'src/hooks/useQueryParams'
import {
  getPeriodOptions,
  PERIOD_OPTIONS_TO_LABELS,
} from 'src/constants/filterPeriods'
import { FilterPeriod } from 'src/constants/filterPeriods/enums/filterPeriod.enum'
import { GlobalQueryParams } from 'src/interfaces/globalQueryParams.interface'
import { createLink } from 'src/utils/createLink'

export interface AiListeningDateRange {
  from: string
  to: string
}

// The top-bar date filter as the from/to the AI Listening dashboard and
// prompts overview take — or undefined for Lifetime (no window: the backend
// covers every check).
//
// Presets are resolved from the period itself, not from ?from/?to: the picker
// clamps those to the account's Social Listening date range, which has
// nothing to do with when AI Listening checks ran. Only Custom carries its
// dates in the URL.
export const useAiListeningDateRange = (): AiListeningDateRange | undefined => {
  const { queryParams } = useQueryParams<GlobalQueryParams>()
  const { from: customFrom, period, to: customTo } = queryParams

  let from: string | undefined
  let to: string | undefined

  if (period === FilterPeriod.CUSTOM) {
    from = customFrom
    to = customTo
  } else if (period && period !== FilterPeriod.LIFETIME) {
    // Day boundaries, so the strings (and the query keys built from them)
    // stay the same between renders.
    const preset = getPeriodOptions()[period]
    from = preset?.start.toISOString(true)
    to = preset?.end.toISOString(true)
  }

  return useMemo(() => {
    if (!from || !to) return undefined
    return { from, to }
  }, [from, to])
}

// The top-bar period for card subtitles: "last 30 days", "lifetime", or
// "the selected period" for a custom range.
export const useAiListeningPeriodLabel = (): string => {
  const { queryParams } = useQueryParams<GlobalQueryParams>()
  const period = queryParams.period as FilterPeriod | undefined
  if (!period || period === FilterPeriod.CUSTOM) return 'the selected period'
  return (
    PERIOD_OPTIONS_TO_LABELS[period] ?? 'the selected period'
  ).toLowerCase()
}

// Builds in-feature links that keep the date filter (a Custom from/to lives
// only in the URL and is lost on a bare path).
export const useAiListeningLink = () => {
  const { queryParams } = useQueryParams<GlobalQueryParams>()
  const { from, period, to } = queryParams

  return useCallback(
    (
      route: string,
      params?: Record<string, string | undefined>,
      extraQuery?: Record<string, string | undefined>
    ) =>
      createLink(route, {
        params,
        queryParams: { from, period, to, ...extraQuery },
      }),
    [from, period, to]
  )
}
